import { Controller, useFormContext } from 'react-hook-form';
import { CardErrorsByField } from '@components/forms/CreditCardForm/utils';
import { PatientAddress } from '@qmd-types/patient';
import BillingAddressForm from './BillingAddressForm';
import {
  CreditCard,
  formatCreditCardNumber,
  formatExpirationNumber,
  unformatCreditCardNumber,
} from './utils';
import { useFraudData } from 'hooks/useFraudData';
import { useMht } from '@pages/SchedulePage/hooks/useMht';
import {
  cn,
  ErrorMessage,
  Field,
  Input,
  Label,
  Paragraph,
} from '@quickmd-llc/design-system';

export interface FormValues {
  creditCardId?: string;
  creditCard: CreditCard;
  sameAsHomeAddress: boolean;
  address: PatientAddress;
}

const mapError = (error: string) => (
  <ErrorMessage key={error}>{error}</ErrorMessage>
);

type Props = {
  cardErrors: CardErrorsByField;
  price?: number;
  hideTitle?: boolean;
  AdyenCardForm?: () => JSX.Element;
};

export function CreditCardForm({
  cardErrors,
  hideTitle,
  price,
  AdyenCardForm,
}: Props) {
  const { control, formState, register, setValue, trigger } =
    useFormContext<FormValues>();
  const { errors } = formState;
  const { creditCard } = errors;
  const { cardNumber, cardExpiration, cardCode } = creditCard || {};
  const { remainingAttemptCount, showFraudCounter } = useFraudData();

  function onChangeCardNumber({
    currentTarget,
  }: React.ChangeEvent<HTMLInputElement>) {
    const value = unformatCreditCardNumber(currentTarget.value);
    setValue('creditCard.cardNumber', value);
  }

  function onChangeCardCode({
    currentTarget,
  }: React.ChangeEvent<HTMLInputElement>) {
    setValue('creditCard.cardCode', currentTarget.value.replace(/\D/g, ''));
  }

  const { isMht } = useMht();
  return (
    <div>
      {!hideTitle && (
        <div className={cn(!isMht && 'mb-2')}>
          <Paragraph className="font-semibold text-text-dark">
            {isMht ? 'Payment method' : 'Pay with card'}
          </Paragraph>
        </div>
      )}

      {isMht && (
        <Paragraph size="sm" className="mb-sp-1 text-text-medium">
          You must enter payment method to begin membership.
        </Paragraph>
      )}

      {cardErrors.general.length > 0 && (
        <Field className="mb-2">{cardErrors.general.map(mapError)}</Field>
      )}
      {AdyenCardForm ? (
        <AdyenCardForm />
      ) : (
        <>
          <Field
            className="mb-2"
            error={Boolean(cardNumber) || cardErrors.cardNumber.length > 0}
          >
            <Label>Card number</Label>
            {cardNumber && (
              <ErrorMessage>A valid card number is required.</ErrorMessage>
            )}
            {cardErrors.cardNumber.map(mapError)}
            <Controller
              control={control}
              name="creditCard.cardNumber"
              render={({ field: { value } }) => (
                <Input
                  placeholder="0000 0000 0000 0000"
                  onBlur={() => trigger('creditCard.cardNumber')}
                  onChange={onChangeCardNumber}
                  type="text"
                  value={formatCreditCardNumber(value)}
                />
              )}
              rules={{ required: price !== 0 }}
            />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field
              error={
                Boolean(cardExpiration) || cardErrors.cardExpiration.length > 0
              }
            >
              <Label>Expiration</Label>
              {cardExpiration && (
                <ErrorMessage>Expiration date is required.</ErrorMessage>
              )}
              {cardErrors.cardExpiration.map(mapError)}
              <Controller
                control={control}
                name="creditCard.cardExpiration"
                render={({ field: { onChange, value } }) => (
                  <Input
                    placeholder="MM/YY"
                    onBlur={() => trigger('creditCard.cardExpiration')}
                    onChange={onChange}
                    type="text"
                    value={formatExpirationNumber(value)}
                  />
                )}
                rules={{ required: price !== 0 }}
              />
            </Field>

            <Field error={Boolean(cardCode) || cardErrors.cardCode.length > 0}>
              <Label>Security Code</Label>
              {cardCode && <ErrorMessage>Card code is required.</ErrorMessage>}
              {cardErrors.cardCode.map(mapError)}
              <Input
                {...register('creditCard.cardCode', { required: price !== 0 })}
                onChange={onChangeCardCode}
                onBlur={() => trigger('creditCard.cardCode')}
                placeholder="0000"
                type="text"
                maxLength={4}
              />
            </Field>
          </div>
        </>
      )}

      <BillingAddressForm />
      {showFraudCounter && (
        <Paragraph className="mt-4 pl-4 pr-4 text-center text-text-error">
          {`You have ${remainingAttemptCount === 1 ? '1 attempt' : `${remainingAttemptCount} attempts`} before your account is locked`}
        </Paragraph>
      )}
    </div>
  );
}
